import * as _ from 'lodash';
import { getLogger } from '../diagnostics/logger';
import moment from 'moment';
import { Action, ActionMeta } from '../actions';
import {
  BULK_SIMS_SELECTED,
  CHANGE_SIMS_SELECTED,
  CLEAR_SELECTED_ACCOUNT,
  CLEAR_SIMS,
  DESELECT_SIMS,
  EXPIRE_SIMS,
  LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_FAILURE,
  LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_PENDING,
  LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_SUCCESS,
  LOAD_ORANGE_PROVISIONING_ORDER_FAILURE,
  LOAD_ORANGE_PROVISIONING_ORDER_PENDING,
  LOAD_ORANGE_PROVISIONING_ORDER_SUCCESS,
  LOAD_PROVISIONING_FAILURE,
  LOAD_PROVISIONING_PENDING,
  LOAD_PROVISIONING_SUCCESS,
  LOAD_SIMS_FAILURE,
  LOAD_SIMS_PENDING,
  LOAD_SIMS_SUCCESS,
  SET_SELECTED_SIMS,
  SET_VIEWED_SIMS
} from '../actions/actionTypes';
import { LoadDirectlyAvailableCommandTypesFilter } from '../actions/directlyAvailableCommandTypeActions';
import { HashMap } from '../constants';
import { GridSelection } from '../models';
import { SimManagementError } from '../models/Errors';
import { BasicPageableSearchFilter } from '../models/searchFilter';
import {
  getSelectedStockSerialNumbers,
  LoadBucketSuccessResponse,
  LoadProvisioningSuccessResponse,
  LoadSimsFailurePayload,
  LoadSimsSuccessPayload,
  LoadStockInfoFailurePayload,
  Sim,
  SimInfo
} from '../models/sim';
import { initialAppState } from '../models/state/appState';
import {
  CommandTypesCacheTimeout,
  getDefaultDirectlyAvailableCommandTypesState,
  DirectlyAvailableSimCommandTypesState
} from '../models/state/directlyAvailableCommandTypeState';
import { getDefaultProductOrderState, OrangeProvisioningOrderCacheTimeout } from '../models/state/bucket';
import {
  getDefaultProvisioningState,
  getDefaultSimsState,
  SimsCacheTimeout,
  SimsState
} from '../models/state/simsState';

const log = getLogger('simManagement.reducers.simReducer');

/**
 * Orders an array of sims by name, ignoring case.
 * @param list
 */
function orderSimList(list: Sim[]): Sim[] {
  return _.orderBy(list, [(d: Sim) => d.raNumber], ['asc']);
}

export default function simReducer(state: SimsState = initialAppState.sims, action: Action<any>): SimsState {
  function getEmptyResults(): Partial<SimsState> {
    return {
      all: { result: [] },
      simsById: {},
      lastLoadedOn: undefined,
      expiresOn: undefined
    };
  }

  switch (action.type) {
    case LOAD_ORANGE_PROVISIONING_ORDER_PENDING: {
      const stockId = action.payload as string;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        orangeProductOrder: {
          ...getDefaultProductOrderState(),
          isLoading: true
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case LOAD_ORANGE_PROVISIONING_ORDER_FAILURE: {
      const payload = action.payload as LoadStockInfoFailurePayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { error, stockId } = payload;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        orangeProductOrder: {
          ...getDefaultProductOrderState(),
          error
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case LOAD_ORANGE_PROVISIONING_ORDER_SUCCESS: {
      const payload = action.payload as LoadBucketSuccessResponse;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { stockId, productOrder } = payload;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        orangeProductOrder: {
          ...getDefaultProductOrderState(),
          ...productOrder,
          expiresOn: moment().add(OrangeProvisioningOrderCacheTimeout, 'seconds').toDate(),
          lastLoadedOn: new Date()
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case LOAD_PROVISIONING_PENDING: {
      const stockId = action.payload as string;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        provisioning: {
          ...getDefaultProvisioningState(),
          isLoading: true
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case LOAD_PROVISIONING_FAILURE: {
      const payload = action.payload as LoadStockInfoFailurePayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { error, stockId } = payload;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        provisioning: {
          ...getDefaultProvisioningState(),
          error
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case LOAD_PROVISIONING_SUCCESS: {
      const payload = action.payload as LoadProvisioningSuccessResponse;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { stockId, provisioning } = payload;
      const sim = state.selected[stockId];
      if (!sim) {
        return state;
      }

      const updatedSim: SimInfo = {
        ...sim,
        provisioning: {
          ...getDefaultProvisioningState(),
          ...provisioning,
          expiresOn: moment().add(SimsCacheTimeout, 'seconds').toDate(),
          lastLoadedOn: new Date()
        }
      };

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        [stockId]: updatedSim
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case CLEAR_SIMS: {
      const newState = getDefaultSimsState();
      return newState;
    }
    case LOAD_SIMS_PENDING: {
      const searchFilter = action.payload as BasicPageableSearchFilter;
      if (!searchFilter) {
        log.warn('No payload');
        return state;
      }

      const changedState: Partial<SimsState> = {
        error: undefined,
        isLoading: true,
        searchFilter
      };

      const clearSearchResults =
        searchFilter.companyId === state.searchFilter.companyId &&
        searchFilter.accountId === state.searchFilter.accountId;
      const changedStateExtra = clearSearchResults ? {} : getEmptyResults();
      const newState = Object.assign({}, state, changedState, changedStateExtra);
      return newState;
    }
    case LOAD_SIMS_FAILURE: {
      const payload = action.payload as LoadSimsFailurePayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { error, searchFilter } = payload;
      if (searchFilter !== state.searchFilter) return state;
      const changedState: Partial<SimsState> = {
        error,
        isLoading: false,
        searchFilter
      };

      const clearSearchResults =
        searchFilter.companyId === state.searchFilter.companyId &&
        searchFilter.accountId === state.searchFilter.accountId;
      const changedStateExtra = clearSearchResults ? {} : getEmptyResults();
      const newState = Object.assign({}, state, changedState, changedStateExtra);
      return newState;
    }
    case LOAD_SIMS_SUCCESS: {
      const payload = action.payload as LoadSimsSuccessPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { searchFilter, sims } = payload;
      // Only the latest request may replace the shared results or selected SIM details.
      if (searchFilter !== state.searchFilter) return state;
      const { startIndex } = searchFilter;
      const loadedResult = sims.result;
      const result = startIndex === 0 ? [] : [...state.all.result];
      for (let i = startIndex; i < startIndex + loadedResult.length; i++) {
        result[i] = {
          ...loadedResult[i - startIndex]
        };
      }

      const simsById = Object.assign({}, ...payload.sims.result.map((a) => ({ [a.stockId]: a })));
      const simsByTel = Object.assign(
        {},
        ...payload.sims.result.filter((s) => s.tel && s.tel.length).map((a) => ({ [a.tel]: a }))
      );

      // update selected SIMs
      const selected: HashMap<SimInfo> = { ...state.selected };
      for (const stockId in selected) {
        if (selected.hasOwnProperty(stockId)) {
          const selectedSim = selected[stockId] as SimInfo;
          const updatedSim = simsById[stockId] || (simsByTel[selectedSim.tel] as SimInfo | undefined);
          if (updatedSim) {
            if (updatedSim.stockId === selectedSim.stockId && updatedSim.tel === selectedSim.tel) {
              // update SIM as the stock is still associated with the same telephone number
              selected[stockId] = updatedSim;
            } else {
              /* Mark the selected SIM as expired. It would be dangerous to update
               * as we may end up performing operations on the wrong line. */
              const simInfo = selected[stockId] as SimInfo;
              selected[stockId] = {
                ...simInfo,
                expired: true
              };
            }
          }
        }
      }

      const changedState: Partial<SimsState> = {
        all: {
          count: sims.count,
          result,
          totalCount: typeof sims.totalCount === 'undefined' ? state.all.totalCount : sims.totalCount
        },
        error: undefined,
        expiresOn: moment().add(SimsCacheTimeout, 'seconds').toDate(),
        isLoading: false,
        lastLoadedOn: new Date(),
        searchFilter,
        selected,
        simsById
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case EXPIRE_SIMS: {
      const payload = action.payload as SimInfo[];
      const expiredById = Object.assign(
        {},
        ...payload.map((a) => ({
          [a.stockId]: {
            ...a,
            expired: true
          }
        }))
      );

      const selected: HashMap<SimInfo> = {
        ...state.selected,
        ...expiredById
      };

      const changedState: Partial<SimsState> = {
        selected
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case BULK_SIMS_SELECTED: {
      const payload = action.payload as SimInfo[];
      if (!payload || !payload.length) {
        log.warn('No payload');
        return state;
      }

      const selected: HashMap<SimInfo> = Object.assign({}, ...payload.map((sim) => ({ [sim.stockId]: sim })));

      const directlyAvailableCommandTypes = getDefaultDirectlyAvailableCommandTypesState();
      const newState = Object.assign({}, state, {
        selected,
        directlyAvailableCommandTypes
      });
      return newState;
    }
    case CHANGE_SIMS_SELECTED: {
      const payload = action.payload as GridSelection<SimInfo>[];
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      // a hash of all SIMs that are currently selected - to be updated
      const selected: HashMap<SimInfo> = Object.assign(
        {},

        ...payload.filter((p) => p.isSelected).map((p) => ({ [p.item.stockId]: p.item }))
      );

      const directlyAvailableCommandTypes = getDefaultDirectlyAvailableCommandTypesState();
      const newState = Object.assign({}, state, {
        selected,
        directlyAvailableCommandTypes
      });
      return newState;
    }
    case SET_SELECTED_SIMS: {
      const payload = action.payload as HashMap<SimInfo>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      // a hash of all SIMs that are currently selected - to be updated
      const selected: HashMap<SimInfo> = {
        ...action.payload
      };

      const directlyAvailableCommandTypes = getDefaultDirectlyAvailableCommandTypesState();
      const newState = Object.assign({}, state, {
        selected,
        directlyAvailableCommandTypes
      });

      return newState;
    }

    case SET_VIEWED_SIMS: {
      const payload = action.payload as HashMap<SimInfo>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      // a hash of all SIMs that are currently selected - to be updated
      const viewed: HashMap<SimInfo> = {
        ...action.payload
      };

      const newState = Object.assign({}, state, {
        viewed
      });

      return newState;
    }
    case DESELECT_SIMS: {
      const typedAction = action as Action<SimInfo[]>;
      const { payload } = typedAction;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const selected: HashMap<SimInfo> = {
        ...state.selected
      };

      payload.forEach((value: any) => {
        delete selected[value.stockId];
      });

      const directlyAvailableCommandTypes = getDefaultDirectlyAvailableCommandTypesState();
      const newState = Object.assign({}, state, {
        selected,
        directlyAvailableCommandTypes
      });
      return newState;
    }
    case CLEAR_SELECTED_ACCOUNT: {
      const newState = Object.assign({}, state, {
        directlyAvailableCommandTypes: getDefaultDirectlyAvailableCommandTypesState()
      });

      return newState;
    }
    case LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_PENDING: {
      const { payload } = action as Action<LoadDirectlyAvailableCommandTypesFilter>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const currentSelection = getSelectedStockSerialNumbers(state.selected, true).join(',');
      if (payload.serialNumbers.join(',') !== currentSelection) {
        return state;
      }

      const directlyAvailableCommandTypes: DirectlyAvailableSimCommandTypesState = {
        ...getDefaultDirectlyAvailableCommandTypesState(),
        isLoading: true
      };

      return Object.assign({}, state, { directlyAvailableCommandTypes });
    }
    case LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_FAILURE: {
      const { meta, payload } = action as ActionMeta<SimManagementError, LoadDirectlyAvailableCommandTypesFilter>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const currentSelection = getSelectedStockSerialNumbers(state.selected, true).join(',');
      if (meta.serialNumbers.join(',') !== currentSelection) {
        return state;
      }

      const directlyAvailableCommandTypes: DirectlyAvailableSimCommandTypesState = {
        ...getDefaultDirectlyAvailableCommandTypesState(),
        error: payload
      };

      return Object.assign({}, state, { directlyAvailableCommandTypes });
    }
    case LOAD_DIRECTLY_AVAILABLE_COMMAND_TYPES_SUCCESS: {
      const { meta, payload } = action as ActionMeta<number[], LoadDirectlyAvailableCommandTypesFilter>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const currentSelection = getSelectedStockSerialNumbers(state.selected, true).join(',');
      if (meta.serialNumbers.join(',') !== currentSelection) {
        return state;
      }

      const changedState: Partial<DirectlyAvailableSimCommandTypesState> = {
        available: [...payload],
        expiresOn: moment().add(CommandTypesCacheTimeout, 'seconds').toDate(),
        isLoading: false,
        lastLoadedOn: new Date()
      };

      const directlyAvailableCommandTypes = Object.assign({}, state.directlyAvailableCommandTypes, changedState);
      return Object.assign({}, state, { directlyAvailableCommandTypes });
    }
    default:
      return state;
  }
}
